<!--
Copyright 2019 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <tr :class="htmlClass">
    <template v-if="frozen">
      <td class="project-form-access-row-form-name">
        <span v-if="form.publishedAt == null" class="icon-edit"
          :title="$t('draftTitle')">
        </span>
        <router-link :to="primaryFormPath(form)" :title="form.nameOrId()">{{ form.nameOrId() }}</router-link>
      </td>
      <td>
        <div class="form-group">
          <select class="form-control"
            :class="{ 'uncommitted-change': stateChanged }"
            :value="changes.current.state" :aria-label="$t('field.state')"
            @change="updateState($event.target.value)">
            <option value="open">{{ $t('formState.open') }}</option>
            <option value="closing">{{ $t('formState.closing') }}</option>
            <option value="closed">{{ $t('formState.closed') }}</option>
          </select>
        </div>
      </td>
    </template>
    <template v-else>
      <td></td>
      <td v-for="fieldKey of fieldKeysWithToken" :key="fieldKey.id"
        class="project-form-access-row-access">
        <div class="checkbox">
          <label>
            <input type="checkbox"
              :class="{ 'uncommitted-change': fieldKeyAccessChanged(fieldKey) }"
              :checked="changes.current.fieldKeyAccess[fieldKey.id]"
              :aria-label="$t('field.appUserAccess')"
              @change="updateFieldKeyAccess(fieldKey, $event.target.checked)">
          </label>
        </div>
      </td>
      <td></td>
    </template>
  </tr>
</template>

<script>
import { mapGetters } from 'vuex';

import Form from '../../../presenters/form';
import routes from '../../../mixins/routes';

export default {
  name: 'ProjectFormAccessRow',
  mixins: [routes()],
  props: {
    form: {
      type: Form,
      required: true
    },
    changes: {
      type: Object,
      required: true
    },
    frozen: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['fieldKeysWithToken']),
    htmlClass() {
      return {
        'project-form-access-row': true,
        'project-form-access-row-draft': this.form.publishedAt == null
      };
    },
    stateChanged() {
      return this.changes.current.state !== this.changes.previous.state;
    }
  },
  methods: {
    fieldKeyAccessChanged(fieldKey) {
      return this.changes.current.fieldKeyAccess[fieldKey.id] !==
        this.changes.previous.fieldKeyAccess[fieldKey.id];
    },
    updateState(state) {
      this.$emit('update:state', this.form, state);
    },
    updateFieldKeyAccess(fieldKey, accessible) {
      this.$emit('update:field-key-access', this.form, fieldKey, accessible);
    }
  }
};
</script>

<style lang="scss">
.project-form-access-row-form-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-form-access-row {
  .form-group {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .form-control { width: 150px; }

  .checkbox {
    margin-bottom: 0;
    margin-left: 6px;
    margin-top: 5px;
  }
}

.project-form-access-row-draft {
  background-color: rgba(0, 0, 0, 0.0225);

  .icon-edit {
    cursor: help;
    margin-right: 9px;
  }
}
</style>

<i18n lang="json5">
{
  "en": {
    "draftTitle": "This Form does not yet have a published version. It will not appear on devices until a Draft is published. Once you publish the Form, the settings shown here will be used.",
    "field": {
      // This is the text of a form field. "State" refers to the Form State.
      // Form States control the lifecycle state of each Form.
      "state": "State",
      "appUserAccess": "App User Access"
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "draftTitle": "Tento formulář zatím nemá publikovanou verzi. Dokud nebude publikován koncept, na zařízeních se nezobrazí. Jakmile formulář publikujete, použijí se zde uvedená nastavení.",
    "field": {
      "state": "Stav",
      "appUserAccess": "Přístup uživatelů aplikace"
    }
  },
  "de": {
    "draftTitle": "Für dieses Formular gibt es noch keine veröffentlichte Version. Auf den Endgeräten wird es erst dann erscheinen, wenn der Entwurf publiziert ist. Sobald Sie das Formular publizieren, werden die hier gezeigten Einstellungen verwendet.",
    "field": {
      "state": "Status",
      "appUserAccess": "Benutzerzugriff"
    }
  },
  "es": {
    "draftTitle": "Este formulario aún no posee una versión publicada. No aparecerá en los dispositivos hasta que un borrador sea publicado. Una vez sea publicado el formulario, los ajustes mostrados aquí serán utilizados.",
    "field": {
      "state": "Estado",
      "appUserAccess": "Acceso de los usuarios móviles"
    }
  },
  "fr": {
    "draftTitle": "Ce formulaire n'a pas encore de version publiée. Il n’apparaîtra pas sur les appareils tant qu'une ébauche ne sera pas publié. Une fois que vous aurez publié le formulaire, les paramètres ci-dessous seront utilisés.",
    "field": {
      "state": "État",
      "appUserAccess": "Accès d'utilisateurs mobiles"
    }
  }
}
</i18n>
